// src/pages/SendEmail.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectUser, setUser } from "../store/authSlice";
import API from "../api";
import { toast } from "react-toastify";
import "../components/Twilio/SendSMS.css";
import "./css/SendEmail.css";


export default function SendEmail() {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);

  const [form, setForm] = useState({ to: "", subject: "", body: "" });
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [connected, setConnected] = useState(false);

  // Read redirect params: connected / needs_reconnect
  const redirectFlags = useMemo(() => {
    const u = new URL(window.location.href);
    return {
      connected: u.searchParams.get("connected"),
      needsReconnect: u.searchParams.get("needs_reconnect"),
    };
  }, []);

  // Hydrate user and connection status
  useEffect(() => {
    (async () => {
      try {
        let current = user;
        if (!current) {
          const { data } = await API.get("/auth/me");
          if (data?.user) {
            current = data.user;
            dispatch(setUser(current));
          }
        }
        if (current?.email) {
          const { data: s } = await API.get("/google/status", { params: { email: current.email } });
          setConnected(!!s?.connected);
        }
      } catch {
        // ignore
      }
    })();
  }, [user, dispatch]);

  // Handle one-time redirect messages, then clean URL
  useEffect(() => {
    const u = new URL(window.location.href);
    let changed = false;

    if (redirectFlags.connected === "1") {
      setConnected(true);
      setMessage("Google connected successfully. You can send emails now.");
      u.searchParams.delete("connected"); changed = true;
    }
    if (redirectFlags.needsReconnect === "1") {
      setConnected(false);
      setMessage("Please reconnect Google once (we need offline access).");
      u.searchParams.delete("needs_reconnect"); changed = true;
    }

    if (changed) window.history.replaceState({}, "", u.toString());
  }, [redirectFlags]);

  const onChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!user?.email) {
      toast.error("You must be logged in to send email.", { autoClose: 5000 });
      return;
    }
    if (!connected) {
      toast.error("Google account not connected. Please connect Google first.", { autoClose: 5000 });
      return;
    }
    try {
      setBusy(true);
      const payload = { to: form.to, subject: form.subject, text: form.body, from: user.email };
      const { data } = await API.post("/google/send", payload);
      setMessage(data?.message || "Sent!");
      setForm({ to: "", subject: "", body: "" });
    } catch (err) {
      const msg = err?.response?.data?.error || "Failed to send email. Please try again.";
      toast.error(`Email failed: ${msg}`, { autoClose: 6000 });
      setMessage(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleConnectGoogle = async (force = false) => {
    try {
      setBusy(true);
      let email = user?.email;
      if (!email) {
        const { data } = await API.get("/auth/me");
        if (data?.user?.email) {
          email = data.user.email;
          dispatch(setUser(data.user));
        } else {
          setMessage("Please log in before connecting Google.");
          return;
        }
      }
      try {
        const { data: s } = await API.get("/google/status", { params: { email } });
        if (s?.connected && !force) {
          setConnected(true);
          setMessage("Google is already connected.");
          return;
        }
      } catch {}
      window.location.href = `https://iso.ringenioussolutions.com/api/google/auth?email=${encodeURIComponent(email)}${force ? "&force=1" : ""}`;
    } catch {
      setMessage("Unable to start Google connection.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="send-email-card" style={{ padding: 16 }}>
      <h2>Send Email</h2>

      {user?.email
        ? <p><strong>Signed in as:</strong> {user.email}</p>
        : <p style={{ color: "tomato" }}>Not signed in.</p>
      }

      <div>
        {connected
          ? <span style={{ color: "green" }}>Google Connected</span>
          : <span style={{ color: "tomato" }}>Google Not Connected</span>
        }
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" onClick={() => handleConnectGoogle(false)} disabled={busy || connected}>
          {connected ? "Connected" : "Connect Google"}
        </button>
        {!connected && (
          <button type="button" onClick={() => handleConnectGoogle(true)} disabled={busy}>
            Force Connect (once)
          </button>
        )}
      </div>

      <form onSubmit={onSubmit} style={{ display: "grid", gap: 8, maxWidth: 480 }}>
        <input
          name="to"
          placeholder="Recipient Email"
          value={form.to}
          onChange={onChange}
          required
          disabled={busy}
        />
        <input
          name="subject"
          placeholder="Subject"
          value={form.subject}
          onChange={onChange}
          required
          disabled={busy}
        />
        <textarea
          name="body"
          placeholder="Message (plain text)"
          value={form.body}
          onChange={onChange}
          required
          disabled={busy}
          rows={6}
        />
        <button type="submit" disabled={busy || !connected}>Send</button>
      </form>

      {message && <p style={{ marginTop: 12 }}>{message}</p>}
    </div>
  );
}
